/**
 * Frontend-local, non-throwing wrapper around the shared `numericToPosition`.
 *
 * `numericToPosition` (packages/shared-types/src/position-mapper.ts) throws on
 * an out-of-range numeric position, and that throwing contract is intentional
 * and must stay unchanged there — the backend depends on it to catch a
 * genuine server-side bug. This module exists only for frontend call sites
 * that must degrade instead of crash when a desynced client legitimately
 * receives an out-of-range position (Story 04-07 / defect D16): skip the
 * offending pawn and keep reconciling the rest of the board, rather than
 * aborting the whole loop.
 */

import type { PlayerColor } from 'shared-types';
import { numericToPosition } from 'shared-types';

/**
 * Same contract as `numericToPosition`, but returns `null` instead of
 * throwing when `numeric` is out of the valid range for `color`. Never
 * throws.
 *
 * @param numeric - Numeric position from Redis / an Ably event.
 * @param color - The player's color.
 * @returns The string position, or `null` if `numeric` is out of range.
 */
export function tryNumericToPosition(numeric: number, color: PlayerColor): string | null {
  try {
    return numericToPosition(numeric, color);
  } catch {
    return null;
  }
}
